"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useState } from "react";
import { useCallbackRef, useIntersected, useSynchronizedScroll, useVirtualPagination } from "../../hooks";
import { classNames } from "../../utils/classNames";
import { CenterAligned } from "../CenterAligned";
import { FlexColumn } from "../FlexColumn";
import { RetryButton } from "../RetryButton";
import { TABLE_SIZES, useTable } from "../Table";
import { TableHeaderWrapper } from "../Table/TableHeader";
import { DEFAULT_PAGINATION_THRESHOLD } from "./constants";
const VirtualizedTableRow = function VirtualizedTableRow(t0) {
  const $ = _c(11);
  const {
    index,
    item,
    Row,
    context,
    translateY
  } = t0;
  const t1 = `translateY(${translateY}px)`;
  let t2;
  if ($[0] !== t1) {
    t2 = {
      transform: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== Row || $[3] !== context || $[4] !== index || $[5] !== item) {
    t3 = <Row context={context} index={index} item={item} />;
    $[2] = Row;
    $[3] = context;
    $[4] = index;
    $[5] = item;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== index || $[8] !== t2 || $[9] !== t3) {
    t4 = <div className="absolute top-0 left-0 grid w-full" data-index={index} style={t2}>{t3}</div>;
    $[7] = index;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};
export const VirtualizedTableView = ({
  className,
  contentClassName,
  items,
  hasNext,
  loadNext,
  paginationThreshold = DEFAULT_PAGINATION_THRESHOLD,
  header,
  renderRow: Row,
  rowVirtualizer,
  setTableRef,
  tableRef,
  id,
  context,
  scrollMode,
  headerBorderOnScroll: headerBorderOnScrollProp = true,
  style,
  contentStyle,
  paginationError,
  gap,
  itemKey
}) => {
  const [headerRef, setHeaderRef] = useCallbackRef();
  const {
    size,
    dividers
  } = useTable();
  const headerRefChild = headerRef.current?.firstElementChild;
  useSynchronizedScroll(headerRefChild, tableRef.current);
  const virtualItems = rowVirtualizer.getVirtualItems();
  const [hasLocalError, setHasLocalError] = useState(false);
  const hasError = paginationError || hasLocalError;
  const onError = useCallback(() => {
    setHasLocalError(true);
  }, []);
  useVirtualPagination({
    items: virtualItems,
    count: items.length,
    loadNext,
    hasNext: hasNext && !hasError,
    paginationThreshold,
    onError
  });
  const isScrolledDown = useIntersected(tableRef, {
    current: headerRefChild
  }, scrollMode === "table" && tableRef || undefined);
  const showBorder = isScrolledDown && headerBorderOnScrollProp || dividers;
  return <FlexColumn aria-rowcount={items.length} className={classNames("w-full min-w-0", className)} id={id} role="table" style={style}>
      {header &&
    // Display contents to allow header to become sticky with parent.
    <TableHeaderWrapper ref={setHeaderRef} showBorder={showBorder}>
          {header}
        </TableHeaderWrapper>}
      <div className={classNames("w-full flex-1 overflow-x-auto", contentClassName)} ref={setTableRef} role="rowgroup" style={contentStyle}>
        <div className="relative" style={{
        height: rowVirtualizer.getTotalSize(),
        // Establish a min height of at least the number of items taking into account gap
        // rowVirtualizer.getTotalSize() doesn't seem to account for this correctly.
        minHeight: items.length * (TABLE_SIZES[size].rowHeight + gap)
      }}>
          {virtualItems.map(({
          index,
          start
        }) => {
          return <VirtualizedTableRow
          // memo breaking generics
          context={context} index={index} item={items[index]} key={itemKey?.(items[index], index) ?? index}
          // virtual items default key is bugged
          Row={Row} translateY={start - (rowVirtualizer.options.scrollMargin ?? 0)} />;
        })}
        </div>

        {(paginationError || hasError) && <CenterAligned className="my-4">
            <RetryButton onClick={async () => {
          await loadNext?.();
          setHasLocalError(false);
        }} />
          </CenterAligned>}
      </div>
    </FlexColumn>;
};